import { DataConfig } from "@/components/table/useDefaultDataConfig";
import { Paginated } from "./types";
import {
  QueryClient,
  QueryFunctionContext,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { generateParams } from "@/utils/helper/generateParams";
import { axiosInstance } from "@/utils/lib/axios";
import { z } from "zod";
import { MAX_CHARACTER_MESSAGE, REQUIRED_MESSAGE } from "@/utils/constant";
import { LoaderFunction, LoaderFunctionArgs } from "react-router-dom";
import { AxiosResponse } from "axios";

type MasterParamData = {
  profileName: string;
  id: string;
  value: string;
  value2?: string | null;
  description?: string | null;
  active: boolean;
  paramType: string;
  paramTag: string;
};

type MasterParamsQueryKey = DataConfig & {
  value?: string;
};

interface MasterParamList extends Paginated {
  content: MasterParamData[];
}

/* ========= *
 * READ Master Param *
 * ========= */

async function getMasterParams(
  queryFunctionContext: QueryFunctionContext,
): Promise<MasterParamList> {
  const { queryKey } = queryFunctionContext;
  const [_, masterParamQueryKey] = queryKey;
  const params = generateParams(masterParamQueryKey as MasterParamsQueryKey);

  const response = await axiosInstance.get("/api/crud/master-param" + params);
  return response.data.data;
}
function useMasterParams(
  masterParamQueryKey: DataConfig = {},
  options?: {
    enabled?: boolean;
  },
) {
  return useQuery({
    ...options,
    queryKey: ["masterParams", masterParamQueryKey],
    queryFn: getMasterParams,
  });
}

interface MasterParamQueryKey {
  id: string;
}

async function getMasterParam(
  queryFunctionContext: QueryFunctionContext,
): Promise<MasterParamData> {
  const { queryKey } = queryFunctionContext;
  const [_, masterParamQueryKey] = queryKey;
  const { id } = masterParamQueryKey as MasterParamQueryKey;

  const response = await axiosInstance.get(`/api/crud/master-param/${id}`);
  return response.data.data;
}
function useMasterParam(masterParamQueryKey: MasterParamQueryKey) {
  const { id } = masterParamQueryKey;
  return useQuery({
    queryKey: ["masterParam", { id }],
    queryFn: getMasterParam,
  });
}
function masterParamLoader(
  queryClient: QueryClient,
): LoaderFunction<MasterParamData> {
  return async ({ params }: LoaderFunctionArgs<{ id: string }>) => {
    const masterParam = await queryClient.ensureQueryData({
      queryKey: ["masterParam", { id: params.id }],
      queryFn: getMasterParam,
    });
    return masterParam;
  };
}

/* =================== *
 * CREATE Master Param *
 * =================== */

const masterParamPayloadSchema = z.object({
  paramType: z
    .string()
    .min(1, { message: REQUIRED_MESSAGE })
    .max(255, { message: MAX_CHARACTER_MESSAGE })
    .transform((val) => (val ? val.toUpperCase() : val)),
  paramTag: z
    .string()
    .max(255, { message: MAX_CHARACTER_MESSAGE })
    .optional()
    .transform((val) => (val ? val.toUpperCase() : val)),
  value: z
    .string()
    .min(1, { message: REQUIRED_MESSAGE })
    .max(255, { message: MAX_CHARACTER_MESSAGE })
    .transform((val) => (val ? val.toUpperCase() : val)),
  value2: z.string().optional().nullable(),
  description: z.string().optional().nullable(),
  active: z.boolean().or(z.string().min(1, { message: REQUIRED_MESSAGE })),
});
type MasterParamPayloadSchema = z.infer<typeof masterParamPayloadSchema>;

async function createMasterParam(
  data: MasterParamPayloadSchema,
): Promise<MasterParamData> {
  const response = await axiosInstance.post("/api/crud/master-param", data);
  return response.data;
}
function useMasterParamCreate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ data }: { data: MasterParamPayloadSchema }) =>
      createMasterParam(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["masterParams"] });
    },
  });
}

/* ================= *
 * EDIT Master Param *
 * ================= */

async function editMasterParam(id: string, data: MasterParamPayloadSchema) {
  const response = await axiosInstance.post(
    `/api/crud/master-param/${id}`,
    data,
  );
  return response.data;
}
function useMasterParamEdit() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: MasterParamPayloadSchema;
    }) => editMasterParam(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["masterParams"] });
    },
  });
}

/* ========= *
 * READ FdtAreaHpm *
 * ========= */

async function getFdtAreaHpms(
  queryFunctionContext: QueryFunctionContext,
): Promise<MasterParamList> {
  const { queryKey } = queryFunctionContext;
  const [_, fdtAreaHpmQueryKey] = queryKey;
  const params = generateParams(fdtAreaHpmQueryKey as MasterParamsQueryKey);

  const response = await axiosInstance.get(
    "/api/crud/master-param" +
      params +
      "&paramType=ITV_FDT_FIBER_AREA_HPM&active=true",
  );
  return response.data.data;
}
function useFdtAreaHpms(
  masterParamQueryKey: MasterParamsQueryKey = {},
  options?: {
    enabled?: boolean;
  },
) {
  return useQuery({
    ...options,
    queryKey: ["fdtAreaHpms", masterParamQueryKey],
    queryFn: getFdtAreaHpms,
  });
}

async function getFibernodeAreaHpms(
  queryFunctionContext: QueryFunctionContext,
): Promise<MasterParamList> {
  const { queryKey } = queryFunctionContext;
  const [_, fibernodeAreaHpms] = queryKey;
  const params = generateParams(fibernodeAreaHpms as MasterParamsQueryKey);

  const response = await axiosInstance.get(
    "/api/crud/master-param" +
      params +
      "&paramType=ITV_FDT_FIBER_AREA_HPM&active=true",
  );
  return response.data.data;
}
function useFibernodeAreaHpms(
  masterParamQueryKey: MasterParamsQueryKey = {},
  options?: {
    enabled?: boolean;
  },
) {
  return useQuery({
    ...options,
    queryKey: ["fibernodeAreaHpms", masterParamQueryKey],
    queryFn: getFibernodeAreaHpms,
  });
}

async function getBranchHpms(
  queryFunctionContext: QueryFunctionContext,
): Promise<MasterParamList> {
  const { queryKey } = queryFunctionContext;
  const [_, branchHpmns] = queryKey;
  const params = generateParams(branchHpmns as MasterParamsQueryKey);

  const response = await axiosInstance.get(
    "/api/crud/master-param" + params + "&paramType=ITV_BRANCH_HPM&active=true",
  );
  return response.data.data;
}
function useBranchHpms(
  masterParamQueryKey: MasterParamsQueryKey = {},
  options?: {
    enabled?: boolean;
  },
) {
  return useQuery({
    ...options,
    queryKey: ["branchHpms", masterParamQueryKey],
    queryFn: getBranchHpms,
  });
}

/* ========= *
 * READ SIGNAL_OPM *
 * ========= */

async function getSignalOPM(
  queryFunctionContext: QueryFunctionContext,
): Promise<MasterParamList> {
  const { queryKey } = queryFunctionContext;
  const [_, branchHpmns] = queryKey;
  const params = generateParams(branchHpmns as MasterParamsQueryKey);

  const response = await axiosInstance.get("/api/crud/master-param" + params);

  return response.data.data;
}

function useSignalOPM(
  masterParamQueryKey: MasterParamsQueryKey = {},
  options?: {
    enabled?: boolean;
  },
) {
  return useQuery({
    ...options,
    queryKey: ["signalOPMs", masterParamQueryKey],
    queryFn: getSignalOPM,
  });
}

/* ========= *
 * READ EDIT OUTPUT FAT *
 * ========= */

async function getAllowEditOutputFats(
  queryFunctionContext: QueryFunctionContext,
): Promise<MasterParamList> {
  const { queryKey } = queryFunctionContext;
  const [_, allowEditOutputFats] = queryKey;
  const params = generateParams(allowEditOutputFats as MasterParamsQueryKey);

  const response = await axiosInstance.get(
    "/api/crud/master-param" + params + "&paramType=ALLOW_EDIT_OUTPUT_FAT",
  );

  return response.data.data;
}

function useAllowEditOuputFat(
  masterParamQueryKey: MasterParamsQueryKey = {},
  options?: {
    enabled?: boolean;
  },
) {
  return useQuery({
    ...options,
    queryKey: ["allowEditOutputFats", masterParamQueryKey],
    queryFn: getAllowEditOutputFats,
  });
}

/* ========= *
 * READ EDIT OUTPUT FAT *
 * ========= */

async function getAllowEditOutputFdts(
  queryFunctionContext: QueryFunctionContext,
): Promise<MasterParamList> {
  const { queryKey } = queryFunctionContext;
  const [_, allowEditOutputFats] = queryKey;
  const params = generateParams(allowEditOutputFats as MasterParamsQueryKey);

  const response = await axiosInstance.get(
    "/api/crud/master-param" + params + "&paramType=ALLOW_EDIT_OUTPUT_FDT",
  );

  return response.data.data;
}

function useAllowEditOuputFdt(
  masterParamQueryKey: MasterParamsQueryKey = {},
  options?: {
    enabled?: boolean;
  },
) {
  return useQuery({
    ...options,
    queryKey: ["allowEditOutputFdts", masterParamQueryKey],
    queryFn: getAllowEditOutputFdts,
  });
}

/* ========= *
 * READ APPROVER *
 * ========= */

async function getApprovers(
  queryFunctionContext: QueryFunctionContext,
): Promise<MasterParamList> {
  const { queryKey } = queryFunctionContext;
  const [_, approvers] = queryKey;
  const params = generateParams(approvers as MasterParamsQueryKey);

  const response = await axiosInstance.get(
    "/api/crud/master-param" + params + "&paramType=APPROVER&active=true",
  );
  return response.data.data;
}
function useApprovers(
  masterParamQueryKey: MasterParamsQueryKey = {},
  options?: {
    enabled?: boolean;
  },
) {
  return useQuery({
    ...options,
    queryKey: ["approvers", masterParamQueryKey],
    queryFn: getApprovers,
  });
}

/* ========= *
 * READ SERVICABLE *
 * ========= */

async function getServicables(
  queryFunctionContext: QueryFunctionContext,
): Promise<MasterParamList> {
  const { queryKey } = queryFunctionContext;
  const [_, approvers] = queryKey;
  const params = generateParams(approvers as MasterParamsQueryKey);

  const response = await axiosInstance.get(
    "/api/crud/master-param" + params + "&paramType=SERVICEABLE&active=true",
  );
  return response.data.data;
}
function useServicables(
  masterParamQueryKey: MasterParamsQueryKey = {},
  options?: {
    enabled?: boolean;
  },
) {
  return useQuery({
    ...options,
    queryKey: ["servicables", masterParamQueryKey],
    queryFn: getServicables,
  });
}

/* ========= *
 * READ DEMOGRAPHIC *
 * ========= */

async function getDemographics(
  queryFunctionContext: QueryFunctionContext,
): Promise<MasterParamList> {
  const { queryKey } = queryFunctionContext;
  const [_, approvers] = queryKey;
  const params = generateParams(approvers as MasterParamsQueryKey);

  const response = await axiosInstance.get(
    "/api/crud/master-param" + params + "&paramType=DEMOGRAPHIC&active=true",
  );
  return response.data.data;
}
function useDemographics(
  masterParamQueryKey: MasterParamsQueryKey = {},
  options?: {
    enabled?: boolean;
  },
) {
  return useQuery({
    ...options,
    queryKey: ["demographics", masterParamQueryKey],
    queryFn: getDemographics,
  });
}

/* ========= *
 * READ Status Integration XL *
 * ========= */

async function getStatusIntegrationXLs(
  queryFunctionContext: QueryFunctionContext,
): Promise<MasterParamList> {
  const { queryKey } = queryFunctionContext;
  const [_, statusIntegrations] = queryKey;
  const params = generateParams(statusIntegrations as MasterParamsQueryKey);

  const response = await axiosInstance.get(
    "/api/crud/master-param" +
      params +
      "&paramType=STATUS_INTEGRATION_XL&active=true",
  );
  return response.data.data;
}
function useStatusIntegrationXLs(
  masterParamQueryKey: MasterParamsQueryKey = {},
  options?: {
    enabled?: boolean;
  },
) {
  return useQuery({
    ...options,
    queryKey: ["statusIntegrationXLs", masterParamQueryKey],
    queryFn: getStatusIntegrationXLs,
  });
}

/* ============= *
 * READ ODN Type *
 * ============= */

async function getOdnTypes(): Promise<MasterParamList> {
  const response = await axiosInstance.get(
    "/api/crud/master-param?paramType=ODN_TYPE",
  );
  return response.data.data;
}
function useOdnTypes() {
  return useQuery({
    queryKey: ["odnTypes"],
    queryFn: getOdnTypes,
  });
}

/* ============= *
 * READ ITV Department *
 * ============= */

async function getITVDepartments(): Promise<MasterParamList> {
  const response = await axiosInstance.get(
    "/api/crud/master-param?paramType=ITV_DEPARTMENT",
  );
  return response.data.data;
}
function useITVDepartments() {
  return useQuery({
    queryKey: ["itvDepartments"],
    queryFn: getITVDepartments,
  });
}

/* ============= *
 * READ OLT Type *
 * ============= */

async function getOltType(): Promise<MasterParamList> {
  const response = await axiosInstance.get(
    "/api/crud/master-param?paramType=ITV_OLT_TYPE",
  );
  return response.data.data;
}
function useOltType() {
  return useQuery({
    queryKey: ["itvOltType"],
    queryFn: getOltType,
  });
}

/* ============= *
 * READ ITV Tenant *
 * ============= */

async function getITVTenant(): Promise<MasterParamList> {
  const response = await axiosInstance.get(
    "/api/crud/master-param?paramType=ITV_TENANT",
  );
  return response.data.data;
}
function useITVTenant() {
  return useQuery({
    queryKey: ["itvTenant"],
    queryFn: getITVTenant,
  });
}

/* ============= *
 * READ ITV Company Code *
 * ============= */

async function getITVCompanyCode(): Promise<MasterParamList> {
  const response = await axiosInstance.get(
    "/api/crud/master-param?paramType=ITV_COMPANY_CODE",
  );
  return response.data.data;
}
function useITVCompanyCode() {
  return useQuery({
    queryKey: ["itvCompanyCode"],
    queryFn: getITVCompanyCode,
  });
}

/* ============= *
 * READ ITV Month Target *
 * ============= */

async function getMonthTarget(): Promise<MasterParamList> {
  const response = await axiosInstance.get(
    "/api/crud/master-param?paramType=MONTH_TARGET",
  );
  return response.data.data;
}
function useMonthTarget() {
  return useQuery({
    queryKey: ["monthTarget"],
    queryFn: getMonthTarget,
  });
}

async function getNotifRetention(): Promise<string> {
  const response = await axiosInstance.get(
    "/api/crud/master-param?paramType=ITV_NOTIF_RETENTION",
  );
  return response.data.data.content[0].value;
}
function useNotifRetention() {
  return useQuery({
    queryKey: ["notifRetention"],
    queryFn: getNotifRetention,
  });
}

/* ============= *
 * READ Check Profile *
 * ============= */

async function getCheckingProfile(): Promise<MasterParamList> {
  const response = await axiosInstance.get(
    "/abdrebuild/api/crud/checkingProfile",
  );
  return response.data.data;
}
function useCheckingProfile() {
  return useQuery({
    queryKey: ["checkingProfile"],
    queryFn: getCheckingProfile,
  });
}

/* =============== *
 * DOWNLOAD Master Param *
 * =============== */

// Utility to poll for media completion
async function pollMediaStatus(
  id: string,
): Promise<{ status: string; path?: string; originalFileName?: string }> {
  const response: AxiosResponse = await axiosInstance.get(
    `/api/crud/media/${id}`,
  );
  return response.data.data;
}

// Wait for media completion with a polling mechanism
async function waitForMediaCompletion(
  id: string,
  interval = 2000,
  pollFn = pollMediaStatus, // Dependency injection for easier testing
): Promise<{ status: string; path?: string; originalFileName?: string }> {
  while (true) {
    const { status, path, originalFileName } = await pollFn(id);

    if (status === "SUCCESS" || status === "ERROR") {
      return { status, path, originalFileName: originalFileName };
    }

    await new Promise((resolve) => setTimeout(resolve, interval));
  }
}

// Utility to handle file download
function downloadFile(
  blobData: Blob,
  fileName: string,
  createObjectURL = window.URL.createObjectURL,
): void {
  const url = createObjectURL(blobData);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", fileName);
  document.body.appendChild(link);
  link.click();
  link.remove();
}

// Export province data
async function exportMasterParam(
  config: DataConfig = {},
  waitFn = waitForMediaCompletion, // Dependency injection for easier testing
  downloadFn = downloadFile, // Dependency injection for easier testing
): Promise<{ status: string }> {
  const createResponse = await axiosInstance.get(
    "/api/crud/master-param/excel",
    {
      params: { ...config },
    },
  );

  const { status, path, originalFileName } = await waitFn(
    createResponse.data.data.id,
  );

  if (status === "SUCCESS") {
    const downloadResponse = await axiosInstance.get(
      "/api/crud/media/download",
      {
        params: { path, fileName: originalFileName },
        responseType: "blob",
      },
    );

    downloadFn(
      new Blob([downloadResponse.data]),
      originalFileName ?? "master-param-list.xlsx",
    );
  }

  return { status };
}

// React Query hook for exporting province
function useExportMasterParam() {
  return useMutation({
    mutationFn: async ({ config }: { config?: DataConfig }) =>
      exportMasterParam(config),
  });
}

/* ============= *
 * READ Get Min Suffix Permit *
 * ============= */

async function getMinSuffixPermitDigit(): Promise<MasterParamList> {
  const response = await axiosInstance.get(
    "/api/crud/master-param?paramType=ITV_MIN_SUFFIX_PERMIT_DIGIT",
  );
  return response.data.data;
}
function useMinSuffixPermitDigit() {
  return useQuery({
    queryKey: ["min-suffix-permit-digit"],
    queryFn: getMinSuffixPermitDigit,
  });
}

/* ============= *
 * READ Get Min Suffix Permit *
 * ============= */

async function getMaxSuffixPermitDigit(): Promise<MasterParamList> {
  const response = await axiosInstance.get(
    "/api/crud/master-param?paramType=ITV_MAX_SUFFIX_PERMIT_DIGIT",
  );
  return response.data.data;
}
function useMaxSuffixPermitDigit() {
  return useQuery({
    queryKey: ["max-suffix-permit-digit"],
    queryFn: getMaxSuffixPermitDigit,
  });
}

/* ============= *
 * READ Get Company Codes *
 * ============= */

async function getCompanyCodes(): Promise<MasterParamList> {
  const response = await axiosInstance.get(
    "/api/crud/master-param?paramType=ITV_COMPANY_CODE&active=true&sort=value&asc=true",
  );
  return response.data.data;
}
function useCompanyCodes() {
  return useQuery({
    queryKey: ["company-codes"],
    queryFn: getCompanyCodes,
  });
}

/* ============= *
 * READ Get Tenant Names *
 * ============= */

async function getTenantsName(): Promise<MasterParamList> {
  const response = await axiosInstance.get(
    "/api/crud/master-param?paramType=ITV_TENANT&active=true&sort=value&asc=true",
  );
  return response.data.data;
}
function useTenantsName() {
  return useQuery({
    queryKey: ["tenants-name"],
    queryFn: getTenantsName,
  });
}

/* ============= *
 * READ Get Dashboard Tableau *
 * ============= */

async function getDashboardTableau(): Promise<MasterParamList> {
  const response = await axiosInstance.get(
    "/api/crud/master-param?paramType=ITV_DASHBOARD_TABLEAU&active=true&sort=value&asc=true",
  );
  return response.data.data;
}
function useDashboardTableau() {
  return useQuery({
    queryKey: ["dashboard-tableau"],
    queryFn: getDashboardTableau,
  });
}

/* ============= *
 * READ Get Dashboard Info *
 * ============= */

async function getDashboardInfo(): Promise<MasterParamList> {
  const response = await axiosInstance.get(
    "/api/crud/master-param?paramType=ITV_DASHBOARD_INFO&active=true&sort=value&asc=true",
  );
  return response.data.data;
}
function useDashboardInfo() {
  return useQuery({
    queryKey: ["dashboard-info"],
    queryFn: getDashboardInfo,
  });
}

/* ============= *
 * READ Get Reason Homepass *
 * ============= */

async function getReasonHomepass(
  queryFunctionContext: QueryFunctionContext,
): Promise<MasterParamList> {
  const { queryKey } = queryFunctionContext;
  const [_, reasonQueryKey] = queryKey;
  const { paramType } = reasonQueryKey as { paramType: string };

  const response = await axiosInstance.get(
    `/api/crud/master-param?paramType=${paramType}&active=true&sort=value&asc=true`,
  );
  return response.data.data;
}

function useReasonHomepass({ paramType }: { paramType: string }) {
  return useQuery({
    queryKey: ["reason-homepass", { paramType }],
    queryFn: getReasonHomepass,
  });
}

/* ============= *
 * READ Get GPON Rx Power Validation *
 * ============= */

async function getGPONRxPowerValidation(): Promise<MasterParamList> {
  const response = await axiosInstance.get(
    "/api/crud/master-param?paramType=ITV_GPON_RX_POWER_MIN,ITV_GPON_RX_POWER_MAX&active=true&sort=value&asc=true",
  );
  return response.data.data;
}

function useGPONRxPowerValidation() {
  return useQuery({
    queryKey: ["gpon-rx-power-validation"],
    queryFn: getGPONRxPowerValidation,
  });
}

function useGPONRxPowerValues() {
  const { data: gponRxPowerValidation } = useGPONRxPowerValidation();

  const minParam = gponRxPowerValidation?.content?.find(
    (param) => param.paramType === "ITV_GPON_RX_POWER_MIN",
  );
  const maxParam = gponRxPowerValidation?.content?.find(
    (param) => param.paramType === "ITV_GPON_RX_POWER_MAX",
  );

  const minValue = minParam ? Number(minParam.value) : undefined;
  const maxValue = maxParam ? Number(maxParam.value) : undefined;

  return { minValue, maxValue };
}

/* ============= *
 * READ Get GPON Distance Range *
 * ============= */

async function getGPONDistanceRange(): Promise<MasterParamList> {
  const response = await axiosInstance.get(
    "/api/crud/master-param?paramType=ITV_GPON_DISTANCE_RANGE&active=true&sort=value&asc=true",
  );
  return response.data.data;
}

function useGPONDistanceRangeParam() {
  return useQuery({
    queryKey: ["gpon-distance-range"],
    queryFn: getGPONDistanceRange,
  });
}

function useGPONDistanceRange(): { distanceRange: number } {
  const { data: gponDistanceRange } = useGPONDistanceRangeParam();

  const param = gponDistanceRange?.content?.find(
    (p) => p.paramType === "ITV_GPON_DISTANCE_RANGE",
  );
  const value = param ? Number(param.value) : NaN;

  return { distanceRange: Number.isNaN(value) ? 10 : value };
}

export type { MasterParamData, MasterParamPayloadSchema };
export {
  masterParamLoader,
  masterParamPayloadSchema,
  useMasterParams,
  useMasterParamCreate,
  useMasterParam,
  useMasterParamEdit,
  useFdtAreaHpms,
  useFibernodeAreaHpms,
  useBranchHpms,
  useSignalOPM,
  useAllowEditOuputFat,
  useAllowEditOuputFdt,
  useApprovers,
  useServicables,
  useDemographics,
  useStatusIntegrationXLs,
  useOdnTypes,
  useITVDepartments,
  pollMediaStatus,
  downloadFile,
  exportMasterParam,
  useExportMasterParam,
  useOltType,
  useITVTenant,
  useITVCompanyCode,
  useMonthTarget,
  useCheckingProfile,
  waitForMediaCompletion,
  useMaxSuffixPermitDigit,
  useMinSuffixPermitDigit,
  useCompanyCodes,
  useTenantsName,
  useDashboardTableau,
  useDashboardInfo,
  useReasonHomepass,
  useGPONRxPowerValues,
  useGPONDistanceRange,
  useNotifRetention,
};
